import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo } from 'react';
import { devWarning, utilStyles, Shimmer } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceXl = tokens.sysSpaceXl,
      sysColorStructureBorderLowContrast = tokens.sysColorStructureBorderLowContrast,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysSpaceXs = tokens.sysSpaceXs;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      elevationStyles = _utilStyles.elevationStyles;

  var _textStyles = textStyles(tokens),
      textHeadingSm = _textStyles.headingSm;

  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: card_base;\n    padding: ", ";\n    position: relative;\n    text-align: initial;\n    background-color: ", ";\n    border: ", " solid\n      ", ";\n    border-radius: ", ";\n    appearance: none;\n\n    &:focus {\n      ", ";\n    }\n  "])), sysSpaceXl, sysColorBackgroundSurface, sysBorderThicknessNormal, sysColorStructureBorderLowContrast, sysBorderRadiusLg, focusStyles.focusInlineElement);
  var clickable = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: clickable;\n    cursor: pointer;\n    width: 100%;\n    appearance: none;\n\n    &:hover {\n      ", "\n    }\n\n    &:active {\n      box-shadow: none;\n    }\n\n    &:focus:active {\n      &:after {\n        content: none;\n      }\n    }\n  "])), elevationStyles.level1); // header styles

  var header = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: header;\n    margin-bottom: ", ";\n    display: table;\n  "])), sysSpaceXl);
  var title = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: title;\n    ", "\n    width: 100%;\n    display: table-cell;\n    margin: initial;\n    padding: ", " 0;\n    text-transform: none;\n    vertical-align: middle;\n  "])), textHeadingSm(), sysSpaceXs); // content styles

  var content = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: content;\n    margin-top: 0.9375rem;\n    margin-bottom: 0.9375rem;\n  "]))); // footer styles

  var footer = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: footer;\n    margin-top: ", ";\n\n    button,\n    a {\n      margin: initial;\n    }\n\n    button:not(:last-child),\n    a:not(:last-child) {\n      margin-right: ", ";\n    }\n\n    [dir='rtl'] & {\n      button:not(:last-child),\n      a:not(:last-child) {\n        margin-right: initial;\n        margin-left: ", ";\n      }\n    }\n  "])), sysSpaceXl, sysSpaceXl, sysSpaceXl); // sizes

  var sizeXs = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: sizeXs;\n    min-width: ", ";\n    min-height: ", ";\n  "])), '10.3125rem', '5rem');
  var sizeXsLong = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: sizeXsLong;\n    min-width: ", ";\n    min-height: ", ";\n  "])), '21.4375rem', '5rem');
  var sizeSm = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: sizeSm;\n    min-width: ", ";\n    min-height: ", ";\n  "])), '10.3125rem', '13.25rem');
  var sizeMd = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: sizeMd;\n    min-width: ", ";\n    min-height: ", ";\n  "])), '21.4375rem', '12.5rem');
  var sizeLg = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: sizeLg;\n    min-width: ", ";\n    min-height: ", ";\n  "])), '21.4375rem', '26.25rem');
  return {
    base: base,
    clickable: clickable,
    header: header,
    title: title,
    content: content,
    footer: footer,
    sizeXs: sizeXs,
    sizeXsLong: sizeXsLong,
    sizeSm: sizeSm,
    sizeMd: sizeMd,
    sizeLg: sizeLg
  };
}

var _excluded = ["onClick", "label", "as", "children", "className", "isLoading", "isInverse", "size"],
    _excluded2 = ["className", "children"],
    _excluded3 = ["className", "children"],
    _excluded4 = ["className", "children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var Card = function (_ref) {
  var onClick = _ref.onClick,
      label = _ref.label,
      _ref$as = _ref.as,
      as = _ref$as === void 0 ? typeof onClick === 'function' ? 'button' : 'div' : _ref$as,
      children = _ref.children,
      className = _ref.className,
      isLoading = _ref.isLoading,
      isInverse = _ref.isInverse,
      size = _ref.size,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();
  var cardStyles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  if (isLoading) {
    return jsx(Shimmer, _extends({
      isInverse: isInverse
    }, rest, {
      "data-ppui": "true"
    }), children);
  }

  var sizeStyleMap = {
    xs: cardStyles.sizeXs,
    'xs-long': cardStyles.sizeXsLong,
    sm: cardStyles.sizeSm,
    md: cardStyles.sizeMd,
    lg: cardStyles.sizeLg
  };

  var props = _objectSpread({}, rest);

  if (label) {
    props['aria-label'] = label;
  }

  return jsx(as, _extends({
    css: [cardStyles.base, sizeStyleMap[size], onClick && cardStyles.clickable],
    className: className,
    "data-ppui-info": "card_2.0.0-ql",
    onClick: onClick
  }, props), children);
};

var Header = function (_ref2) {
  var className = _ref2.className,
      children = _ref2.children,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  return jsx("header", _extends({
    css: _styles.header,
    className: className
  }, rest, {
    "data-ppui": "true"
  }), jsx("h3", {
    css: _styles.title,
    className: className,
    "data-ppui": "true"
  }, children));
};

var Content = function (_ref3) {
  var className = _ref3.className,
      children = _ref3.children,
      rest = _objectWithoutProperties(_ref3, _excluded3);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  return jsx("div", _extends({
    css: _styles.content,
    className: className
  }, rest, {
    "data-ppui": "true"
  }), children);
};

var Footer = function (_ref4) {
  var className = _ref4.className,
      children = _ref4.children,
      rest = _objectWithoutProperties(_ref4, _excluded4);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  return jsx("footer", _extends({
    css: _styles.footer,
    className: className
  }, rest, {
    "data-ppui": "true"
  }), children);
};

Card.Header = Header;
Card.Content = Content;
Card.Footer = Footer;

export { Card, styles as cardStyles };
